import StoreModuleService from '../StoreModuleService';
import { MODULE_NAME } from './const';
import state from './state';
import {
	SET_CREDENTIAL_DATA,
	SET_ACTIVE_FETCH,
	DELETE_USER_CREDENTIALS,
	UPDATE_CONNECTION_TEST,
	UPDATE_CREDENTIALS_NAME,
	SET_ALL_CREDENTIALS_DATA,
	SET_CREDENTIALS_SUGGESTIONS,
	SET_PROJECT_CREDENTIALS_DATA,
	SET_LOCKED_CREDENTIALS,
	SET_USER_MATCHING_DATA,
	BULK_SET_CREDENTIALS_DATA,
} from './mutations';
import api from '@/api';
import { arrayToObject, isNullOrUndefined } from '@/utils/helpers';
import isEmpty from 'lodash/isEmpty';
import { workflowSharedData, getWorkflow } from '@/store/modules/workflows/getters';
import { isConfigurationsOn } from '@/store/modules/featureFlags/getters';
import AppDialogManagerService from '@/components/dialogs/AppDialogManager/app-dialog-manager-service';
import { selectedProjectId } from '../selectedProject/getters';
import { ALL_PROJECTS_VIRTUAL_ID, PROJECTS_ENTITY } from '@/utils/projects.const';
import { setWorkflowShareData, setAllWorkflowData } from '@/store/modules/workflows/actions';

const buildCredentialsActionObject = (action) => StoreModuleService.buildActionObject(MODULE_NAME, action);

// actions declarations
export const setCredentialData = buildCredentialsActionObject('setCredentialData');
export const getCredentialData = buildCredentialsActionObject('getCredentialData');
export const getUserCredentials = buildCredentialsActionObject('getUserCredentials');
export const getProjectCredentials = buildCredentialsActionObject('getProjectCredentials');
export const deleteUserCredentials = buildCredentialsActionObject('deleteUserCredentials');
export const testUserCredentials = buildCredentialsActionObject('testUserCredentials');
export const updateUserCredentialsName = buildCredentialsActionObject('updateUserCredentialsName');
export const setAllCredentialsData = buildCredentialsActionObject('setAllCredentialsData');
export const openSuggestionsToMoveCredentialsToUserConfiguration = buildCredentialsActionObject(
	'openSuggestionsToMoveCredentialsToUserConfiguration'
);
export const acceptCredentialsSuggestions = buildCredentialsActionObject('acceptCredentialsSuggestions');
export const updateCredentialsData = buildCredentialsActionObject('updateCredentialsData');
export const getWorkflowLockedCredentials = buildCredentialsActionObject('getWorkflowLockedCredentials');
export const setUserMatchingData = buildCredentialsActionObject('setUserMatchingData');
export const getCredentialsSuggestions = buildCredentialsActionObject('getCredentialsSuggestions');

const actions = {
	...StoreModuleService.createDefaultActions(),
	async [getCredentialData.action]({ dispatch }, payload) {
		const { id, forceGet } = payload;
		let data = state.data[id];
		const dataIsMissing = isNullOrUndefined(data);
		const needToGet = dataIsMissing || forceGet;
		if (needToGet) {
			const ret = await api.getCredential(id);
			data = ret;
			await dispatch(setCredentialData.action, { data });
		}
		return data;
	},
	[setCredentialData.action]({ commit }, payload) {
		const { data: credentialData } = payload;
		commit(SET_CREDENTIAL_DATA, { key: credentialData.id, value: credentialData });
	},
	[setAllCredentialsData.action]({ commit }, payload) {
		const { data: credentialsData } = payload;
		const credentialsDataMap = credentialsData.reduce(
			(credentialsData, credentialData) => ({
				...credentialsData,
				...{ [credentialData.id]: credentialData },
			}),
			{}
		);
		commit(SET_ALL_CREDENTIALS_DATA, { data: credentialsDataMap });
	},
	async [getUserCredentials.action]({ commit, rootState }, payload) {
		if (!rootState.auth.user) {
			return;
		}
		const { forceGet, filterBy, ...queryParams } = payload;
		// Those params are destrctured and deleted, so we can send the whole payload params to the api call in the api call
		if (state.activeFetch && !forceGet) {
			return;
		}
		const dataIsMissing = isEmpty(state.data);
		const needToGet = dataIsMissing || forceGet;
		if (needToGet) {
			commit(SET_ACTIVE_FETCH, true);
			try {
				const params = {
					...(filterBy || {}),
					...queryParams,
				};
				const credentials = await api.getUserCredentials(params);
				commit(BULK_SET_CREDENTIALS_DATA, { data: arrayToObject(credentials) });
			} catch (err) {
				console.error('Error fetching user credentials', err);
				throw new Error(err);
			} finally {
				commit(SET_ACTIVE_FETCH, false);
			}
		}
	},
	async [getProjectCredentials.action]({ commit, rootState }, payload) {
		if (!rootState.auth.user) {
			return;
		}
		const { forceGet, projectId } = payload;
		if (!projectId) {
			return;
		}
		const dataIsMissing = !state.projectCredentials[projectId]; //not checking for empty - checking if we initialized it ever. if could be initialized but still empty
		const needToGet = dataIsMissing || forceGet;
		if (needToGet) {
			const credentials = await api.getProjectCredentials(projectId);
			commit(SET_PROJECT_CREDENTIALS_DATA, { projectId, data: credentials });
			commit(BULK_SET_CREDENTIALS_DATA, { data: arrayToObject(credentials) });
		}
	},
	async [deleteUserCredentials.action]({ commit }, payload) {
		const { id } = payload;
		await api.disconnectCredentials(id);
		commit(DELETE_USER_CREDENTIALS, id);
	},
	async [testUserCredentials.action]({ commit }, payload) {
		const { id } = payload;
		const ret = await api.testConnection(id);
		commit(UPDATE_CONNECTION_TEST, { id, response: ret });
	},
	async [updateUserCredentialsName.action]({ commit }, payload) {
		const { id, name } = payload;
		const ret = await api.updateCredentialsName(id, name);
		commit(UPDATE_CREDENTIALS_NAME, { id, name: ret.connection_name });
	},
	async [getCredentialsSuggestions.action]({ commit, rootGetters }, payload) {
		const { workflowId, fromPublishFlow } = payload;
		const workflow = rootGetters[getWorkflow.fullPath](workflowId);
		const workflowVersionId = fromPublishFlow ? workflow.current_draft.id : workflow.current_version;
		const results = await api.getWorkflowCredentialsSuggestions(workflowId, workflowVersionId);
		commit(SET_CREDENTIALS_SUGGESTIONS, { workflowId, suggestions: results });
		return results;
	},
	async [openSuggestionsToMoveCredentialsToUserConfiguration.action]({ rootGetters, dispatch }, payload) {
		const { workflow, isFromPublish, next } = payload;
		const workflowIsPublished = workflow.is_published;
		const isConfigurationsFlagOn = rootGetters[isConfigurationsOn.fullPath];
		if (isConfigurationsFlagOn) {
			let sharedData = rootGetters[workflowSharedData.fullPath](workflow.id);
			if (!sharedData && workflowIsPublished) {
				await dispatch(setWorkflowShareData.fullPath, { id: workflow.id, force: true }, { root: true });
				sharedData = rootGetters[workflowSharedData.fullPath](workflow.id);
			}
			const workflowIsShared = workflowIsPublished && (sharedData?.shared_users?.length > 1 || sharedData?.shared_groups?.length > 0); // shared can be only if already published, shared users holds the creator too, so we check greater then 1
			const isFromShareAndWorkflowIsPublished = !isFromPublish && workflowIsPublished;
			const isFromPublishAndWorkflowIsShared = isFromPublish && workflowIsShared;
			const needsToShowSuggestions = isFromPublishAndWorkflowIsShared || isFromShareAndWorkflowIsPublished;

			if (needsToShowSuggestions && isFromPublish) {
				const results = await dispatch(getCredentialsSuggestions.action, { workflowId: workflow.id, fromPublishFlow: isFromPublish });
				const hasNodesForSuggestions =
					results?.move_to_user_configuration?.length > 0 ||
					Object.keys(results?.search_fields_to_move_to_user_configuration || {}).length > 0;

				if (hasNodesForSuggestions) {
					return await AppDialogManagerService.openSuggestCredentialsTypeDialog({
						workflow,
						isFromPublish,
						next,
					});

					// if we do show suggestions, it will call 'next' after suggesting
				}
			}
		}

		// if we don't show suggestions we need to call 'next'
		await next();
	},
	async [acceptCredentialsSuggestions.action]({ dispatch, rootGetters }, payload) {
		const { workflowId, nodesToChange, markNotToSuggest, fromPublishFlow } = payload;
		const workflow = rootGetters[getWorkflow.fullPath](workflowId);
		const workflowVersionId = fromPublishFlow ? workflow.current_draft.id : workflow.current_version;
		const ret = await api.updateWorkflowCredentialsAccordingToSuggestions(workflowId, nodesToChange, markNotToSuggest, workflowVersionId);
		await dispatch(setAllWorkflowData.fullPath, { data: ret, forceGetNodes: true }, { root: true });
	},
	async [updateCredentialsData.action]({ dispatch, rootGetters }, payload) {
		const { data } = payload;
		const ret = await api.updateCredentialAccess(data.id, data.access);
		await dispatch(setCredentialData.action, { data: ret });
		if (data?.access?.related_entity_id && data?.access?.related_entity_type === PROJECTS_ENTITY) {
			await dispatch(getProjectCredentials.action, { forceGet: true, projectId: data.access.related_entity_id });
		} else if (rootGetters[selectedProjectId.fullPath] && rootGetters[selectedProjectId.fullPath] !== ALL_PROJECTS_VIRTUAL_ID) {
			await dispatch(getProjectCredentials.action, { forceGet: true, projectId: rootGetters[selectedProjectId.fullPath] });
		}
	},
	async [getWorkflowLockedCredentials.action]({ commit, state }, payload) {
		const { workflowId, credentialsId } = payload;
		const cachedData = state.lockedCredentialsData[workflowId];
		if (cachedData && cachedData.id === credentialsId) {
			return cachedData;
		}
		const ret = await api.getWorkflowLockedCredentials(workflowId, credentialsId);
		commit(SET_LOCKED_CREDENTIALS, { data: ret });
		return ret;
	},
	async [setUserMatchingData.action]({ commit, state }, payload) {
		const { credentialsId, force } = payload;
		const needToGet = force || !state.userMatching[credentialsId];

		if (!needToGet) {
			return state.userMatching[credentialsId];
		}

		const res = await api.getUserMatchingUsers(credentialsId);
		commit(SET_USER_MATCHING_DATA, { credentialsId, data: res });
		return res;
	},
};

export default actions;
